import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * LazyImage component that uses Intersection Observer to load images only when they come into view
 * @param {Object} props - Component props
 * @param {string} props.src - Image source URL
 * @param {string} props.alt - Image alt text
 * @param {string} props.className - CSS class name for the img element
 * @param {string} props.placeholderSrc - Optional placeholder image to show while loading
 * @param {number} props.retryCount - Number of times to retry loading on error (default: 1)
 * @param {number} props.retryDelay - Delay in ms between retries (default: 2000)
 */
const LazyImage = ({ 
  src, 
  alt, 
  className,
  placeholderSrc,
  retryCount = 1,
  retryDelay = 2000,
  ...props 
}) => {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [retries, setRetries] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(src);
  const imgRef = useRef();
  const timerRef = useRef();

  // Reset states when src changes
  useEffect(() => {
    if (src) {
      setIsLoaded(false);
      setError(false);
      setRetries(0);
      setCurrentSrc(src);
    }
    
    // Clear any pending retry timers
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [src]);

  // Handle image error event with retry logic
  const handleImageError = useCallback(() => {
    if (retries < retryCount) {
      // Clear previous timer if it exists
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      
      console.warn(`Failed to load image (attempt ${retries + 1}/${retryCount + 1}): ${src}`);
      
      // Set timer for retry with cache-busting
      timerRef.current = setTimeout(() => {
        // Add cache busting parameter
        const cacheBuster = `_cb=${Date.now()}`;
        const newSrc = src.includes('?') ? `${src}&${cacheBuster}` : `${src}?${cacheBuster}`;
        setCurrentSrc(newSrc);
        setRetries(prev => prev + 1);
      }, retryDelay);
    } else {
      // All retries failed
      setError(true);
      console.error(`Failed to load image after ${retryCount + 1} attempts: ${src}`);
    }
  }, [retries, retryCount, retryDelay, src]);

  // Handle image load success
  const handleImageLoad = () => {
    setIsLoaded(true);
    setError(false);
    
    // Clear any pending retry timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  // Set up Intersection Observer
  useEffect(() => {
    // Skip if no src or browser doesn't support IntersectionObserver
    if (!src || typeof IntersectionObserver === 'undefined') {
      // If IntersectionObserver is not supported, just load the image
      setIsInView(true);
      return;
    }
    
    const currentImgRef = imgRef.current;
    
    // Create an observer instance
    const observer = new IntersectionObserver(
      (entries) => {
        // If the image is in view, start loading it
        if (entries[0].isIntersecting) {
          setIsInView(true);
          // Unobserve after detection
          observer.unobserve(currentImgRef);
        }
      },
      {
        // Options: trigger when image is near viewport (200px margin)
        rootMargin: '200px 0px',
        threshold: 0.01, // Trigger when even 1% of the element is visible
      }
    );

    // Start observing the image element
    if (currentImgRef) {
      observer.observe(currentImgRef);
    }

    // Clean up the observer when component unmounts
    return () => {
      if (currentImgRef && observer) {
        observer.unobserve(currentImgRef);
      }
    };
  }, [src]);

  return (
    <div 
      ref={imgRef} 
      className={`lazy-image-container ${isLoaded ? 'loaded' : 'loading'} ${error ? 'error' : ''}`}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      {/* Show a loading placeholder until image is loaded */}
      {!isLoaded && !error && (
        <div className="lazy-image-placeholder">
          {placeholderSrc ? (
            <img 
              src={placeholderSrc} 
              alt={alt ? `${alt} placeholder` : 'Loading'} 
              className="lazy-image-placeholder-img"
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <div className="lazy-image-spinner"></div>
          )}
        </div>
      )}
      
      {/* Show error placeholder if image failed to load */}
      {error && (
        <div className="lazy-image-error">
          <i className="fa fa-exclamation-triangle"></i>
          <span className="error-message">Image not available</span>
        </div>
      )}
      
      {/* Only set src when in view to trigger loading */}
      {isInView && !error && (
        <img
          src={currentSrc}
          alt={alt || ''}
          className={className}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
          {...props}
        />
      )}
    </div>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default React.memo(LazyImage, (prevProps, nextProps) => {
  // Only re-render if the src changed
  return prevProps.src === nextProps.src;
});
